import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import { formatDate } from "@/date"
import { isLowStock } from "@/supplies"
import {
  StockActionDialog,
  type StockActionSubmit,
} from "@/supplies/components/stock-action-dialog"
// Alias useSupplyStockFn: name starts with "use" and trips rules-of-hooks lint.
import {
  adjustSupplyStockFn,
  useSupplyStockFn as consumeSupplyStockFn,
  getSupplyItemFn,
  listSupplyMovementsFn,
} from "@/supplies/rpc"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

export const Route = createFileRoute(
  "/_supplies-layout/supplies/items/$itemId",
)({
  loader: async ({ params }) => {
    const id = Number(params.itemId)
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error("Invalid item id")
    }
    return getSupplyItemFn({ data: { id } })
  },
  component: ItemDetailPage,
})

interface SupplyItemDetail {
  id: number
  name: string
  sku: string | null
  categoryId: number
  unit: string
  quantityOnHand: number
  reorderLevel: number | null
  unitCostCents: number | null
  notes: string | null
  isActive: boolean
  createdAt: number
  updatedAt: number
  category?: { id: number; name: string } | null
}

interface MovementRow {
  id: number
  type: "receive" | "use" | "adjust"
  quantityDelta: number
  quantityAfter: number
  note: string | null
  invoiceNumber: string | null
  unitCostCents: number | null
  createdAt: number
  createdByUserId: number
  vendor?: { id: number; name: string } | null
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—"
  return `$${(cents / 100).toFixed(2)}`
}

function formatDateTime(ms: number): string {
  const date = new Date(ms)
  const day = formatDate(date)
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date)
  return `${day} ${time}`
}

function movementTypeLabel(type: MovementRow["type"]): string {
  switch (type) {
    case "receive":
      return "Receive"
    case "use":
      return "Use"
    case "adjust":
      return "Adjust"
  }
}

function ItemDetailPage() {
  const item = Route.useLoaderData() as SupplyItemDetail
  const queryClient = useQueryClient()
  const router = useRouter()
  const [actionMode, setActionMode] = useState<"use" | "adjust" | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const movementsQuery = useQuery({
    queryKey: ["supply-movements", { itemId: item.id }],
    queryFn: () =>
      listSupplyMovementsFn({
        data: { itemId: item.id, pageSize: 50 },
      }),
  })

  const useStock = useMutation({
    mutationFn: (input: { quantity: number; note: string | null }) =>
      consumeSupplyStockFn({
        data: {
          itemId: item.id,
          quantity: input.quantity,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setActionError(null)
      setActionMode(null)
      await queryClient.invalidateQueries({
        queryKey: ["supply-movements", { itemId: item.id }],
      })
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
      await queryClient.invalidateQueries({ queryKey: ["supply-movements"] })
      await queryClient.invalidateQueries({ queryKey: ["supplies-dashboard"] })
      await router.invalidate()
    },
    onError: (error) => setActionError(getErrorMessage(error)),
  })

  const adjustStock = useMutation({
    mutationFn: (input: { quantityDelta: number; note: string }) =>
      adjustSupplyStockFn({
        data: {
          itemId: item.id,
          quantityDelta: input.quantityDelta,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setActionError(null)
      setActionMode(null)
      await queryClient.invalidateQueries({
        queryKey: ["supply-movements", { itemId: item.id }],
      })
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
      await queryClient.invalidateQueries({ queryKey: ["supply-movements"] })
      await queryClient.invalidateQueries({ queryKey: ["supplies-dashboard"] })
      await router.invalidate()
    },
    onError: (error) => setActionError(getErrorMessage(error)),
  })

  const isSubmitting = useStock.isPending || adjustStock.isPending
  const low = isLowStock(item)
  const movements = (movementsQuery.data?.movements ?? []) as MovementRow[]

  function openAction(mode: "use" | "adjust") {
    setActionError(null)
    setActionMode(mode)
  }

  async function handleActionSubmit(payload: StockActionSubmit) {
    if (isSubmitting || actionMode == null) return
    setActionError(null)

    try {
      if (actionMode === "use") {
        if (payload.quantity == null) return
        await useStock.mutateAsync({
          quantity: payload.quantity,
          note: payload.note ?? null,
        })
      } else {
        if (payload.quantityDelta == null || !payload.note) return
        await adjustStock.mutateAsync({
          quantityDelta: payload.quantityDelta,
          note: payload.note,
        })
      }
    } catch {
      // surfaced via onError
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-muted-foreground text-sm">
            <Link
              to="/supplies/items"
              className="text-primary underline-offset-4 hover:underline"
            >
              Items
            </Link>
            <span className="mx-1.5">/</span>
            <span>{item.name}</span>
          </p>
          <h1 className="mt-1 font-semibold text-2xl tracking-tight">
            {item.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {item.isActive ? (
              <Badge variant="secondary">Active</Badge>
            ) : (
              <Badge variant="outline">Inactive</Badge>
            )}
            {low ? <Badge variant="destructive">Low stock</Badge> : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={!item.isActive}
            onClick={() => openAction("use")}
          >
            Use
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!item.isActive}
            onClick={() => openAction("adjust")}
          >
            Adjust
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Item</CardTitle>
            <CardDescription>Catalog fields</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">SKU</dt>
                <dd className="font-medium font-mono">{item.sku || "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Category</dt>
                <dd className="font-medium">{item.category?.name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Unit</dt>
                <dd className="font-medium">{item.unit}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Unit cost</dt>
                <dd className="font-medium tabular-nums">
                  {formatCents(item.unitCostCents)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Reorder level</dt>
                <dd className="font-medium tabular-nums">
                  {item.reorderLevel ?? "—"}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">Notes</dt>
                <dd className="font-medium">{item.notes?.trim() || "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Created</dt>
                <dd className="font-medium">
                  {formatDateTime(item.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Updated</dt>
                <dd className="font-medium">
                  {formatDateTime(item.updatedAt)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Stock on hand</CardTitle>
            <CardDescription>Current quantity available</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-4xl tabular-nums tracking-tight">
              {item.quantityOnHand}
            </p>
            <p className="mt-1 text-muted-foreground text-sm">{item.unit}</p>
            {item.reorderLevel != null ? (
              <p className="mt-3 text-sm">
                Reorder at{" "}
                <span className="font-medium tabular-nums">
                  {item.reorderLevel}
                </span>
                {low ? (
                  <span className="ml-2 text-destructive">
                    (at or below reorder)
                  </span>
                ) : null}
              </p>
            ) : (
              <p className="mt-3 text-muted-foreground text-sm">
                No reorder level set.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Movement history</CardTitle>
          <CardDescription>
            Recent receive, use, and adjust ledger rows
          </CardDescription>
        </CardHeader>
        <CardContent>
          {movementsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : movementsQuery.isError ? (
            <p className="text-destructive text-sm" role="alert">
              {getErrorMessage(movementsQuery.error)}
            </p>
          ) : movements.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No movements yet. Receive stock to start the ledger.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="border-b bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Delta</th>
                    <th className="px-3 py-2 font-medium">After</th>
                    <th className="px-3 py-2 font-medium">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {movements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {formatDateTime(movement.createdAt)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline">
                          {movementTypeLabel(movement.type)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-medium tabular-nums">
                        {movement.quantityDelta > 0
                          ? `+${movement.quantityDelta}`
                          : movement.quantityDelta}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground tabular-nums">
                        {movement.quantityAfter}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {movement.note ||
                          (movement.vendor ? movement.vendor.name : null) ||
                          (movement.invoiceNumber
                            ? `Inv ${movement.invoiceNumber}`
                            : null) ||
                          "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {actionMode != null ? (
        <StockActionDialog
          open
          onOpenChange={(open) => {
            if (!open && !isSubmitting) {
              setActionMode(null)
              setActionError(null)
            }
          }}
          mode={actionMode}
          itemName={item.name}
          quantityOnHand={item.quantityOnHand}
          isSubmitting={isSubmitting}
          error={actionError}
          onSubmit={handleActionSubmit}
        />
      ) : null}
    </div>
  )
}
